import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Card,
  Statistic,
  Input,
  Select,
  DatePicker,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkExecutionAside from "./workExecutionAside";
import { formatCurrency } from "../../utils/Utils";
import { mockWorkOrders } from "../../data/mockWorkOrders";

const { Option } = Select;
const { RangePicker } = DatePicker;

const WorkExecutionUpdates: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    contractType: "",
    organisation: "",
    contractor: "",
    woValueRange: "",
    dateRange: null as any,
    slaStatus: "",
  });

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Accepted: "success",
      "In Execution": "processing",
      Finalized: "success",
      "Pending Execution Approval": "warning",
      Rejected: "error",
      "Under Negotiation": "processing",
    };
    return statusColors[status] || "default";
  };

  const handleViewSections = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/work-execution-work-order-details/${record.workOrderId}`
    );
  };

  const resetFilters = () => {
    setFilters({
      contractType: "",
      organisation: "",
      contractor: "",
      woValueRange: "",
      dateRange: null,
      slaStatus: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalValue = workOrders.reduce((sum, wo) => sum + wo.woValue, 0);
  const inExecutionCount = workOrders.filter(
    (wo) => wo.status === "In Execution"
  ).length;
  const avgSlaAge =
    workOrders.length > 0
      ? Math.round(
          workOrders.reduce((sum, wo) => sum + wo.slaAge, 0) / workOrders.length
        )
      : 0;
  const avgProgress =
    workOrders.length > 0
      ? Math.round(
          workOrders.reduce(
            (sum, wo) => sum + (wo.progressPercentage || 0),
            0
          ) / workOrders.length
        )
      : 0;

  const columns: TableColumnsType<any> = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`woStatus-${record?.workOrderId}`}
          onClick={() => handleViewSections(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Work Order Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 280,
      render: (text, record) => (
        <div>
          <div className="fw-bold">{text}</div>
          <div className="text-muted small mt-1">
            {record.siteName}
            {record.projectName && ` • ${record.projectName}`}
          </div>
        </div>
      ),
    },
    // {
    //   title: "Site/Project",
    //   key: "siteProject",
    //   width: 200,
    //   render: (_, record) => (
    //     <div>
    //       <div className="fw-bold">{record.site}</div>
    //       <div className="text-muted small">{record.project}</div>
    //     </div>
    //   ),
    // },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    // {
    //   title: "Sections",
    //   key: "sections",
    //   width: 150,
    //   render: (_, record) => (
    //     <div>
    //       <div className="fw-bold">3 Sections</div>
    //       <div className="text-muted small">2 Updatable</div>
    //     </div>
    //   ),
    // },
    {
      title: "Progress %",
      key: "progressPercent",
      width: 120,
      align: "center",
      render: (_, record) => {
        const progress =
          record.progressPercent || Math.floor(Math.random() * 80) + 10;
        return (
          <div>
            <div className="fw-bold">{progress}%</div>
            <div
              style={{
                width: "100%",
                height: "4px",
                backgroundColor: "#f0f0f0",
                borderRadius: "2px",
                marginTop: "4px",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor:
                    progress > 70
                      ? "#52c41a"
                      : progress > 40
                      ? "#faad14"
                      : "#ff4d4f",
                  borderRadius: "2px",
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Last Exec Update",
      key: "lastUpdate",
      width: 140,
      render: (_, record) => (
        <div>
          <div>
            {dayjs()
              .subtract(Math.floor(Math.random() * 10) + 1, "days")
              .format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "Age (Days)",
      dataIndex: "slaAgingForConfirmation",
      key: "slaAgingForConfirmation",
      width: 100,
      align: "center",
      render: (days) => (
        <Badge
          count={`${days}d`}
          color={days > 30 ? "#ff4d4f" : days > 15 ? "#faad14" : "#52c41a"}
        />
      ),
      sorter: (a, b) => a.slaAgingForConfirmation - b.slaAgingForConfirmation,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            onClick={() => handleViewSections(record)}
          >
            Update Work
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-execution-under-execution"
      title="Work Execution Updates"
      columns={columns}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkExecutionAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1600,
        showSearch: true,
        searchPlaceholder: "Search by WO#, Title, or Contractor...",
        kpiWidgets: [
          {
            title: "Total Eligible WOs",
            value: workOrders.length,
            icon: <ClockCircleOutlined />,
            color: "#1890ff",
          },
          {
            title: "Total Value",
            value: formatCurrency(totalValue, "INR"),
            icon: <DollarOutlined />,
            color: "#52c41a",
          },
          {
            title: "In Execution",
            value: inExecutionCount,
            icon: <CalendarOutlined />,
            color: "#faad14",
          },
          {
            title: "Avg Progress",
            value: `${avgProgress}%`,
            icon: <UserOutlined />,
            color: "#722ed1",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Contract Type</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contract Type"
                    value={filters.contractType}
                    onChange={(value) =>
                      setFilters({ ...filters, contractType: value })
                    }
                  >
                    <Option value="Fixed Price">Fixed Price</Option>
                    <Option value="Time & Material">Time & Material</Option>
                    <Option value="Cost Plus">Cost Plus</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Organisation</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Organisation"
                    value={filters.organisation}
                    onChange={(value) =>
                      setFilters({ ...filters, organisation: value })
                    }
                  >
                    <Option value="ABC Corp">ABC Corp</Option>
                    <Option value="XYZ Ltd">XYZ Ltd</Option>
                    <Option value="DEF Industries">DEF Industries</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="TechBuild Solutions">
                      TechBuild Solutions
                    </Option>
                    <Option value="InfraMax Ltd">InfraMax Ltd</Option>
                    <Option value="BuildPro Services">BuildPro Services</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">WO Value Range</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Value Range"
                    value={filters.woValueRange}
                    onChange={(value) =>
                      setFilters({ ...filters, woValueRange: value })
                    }
                  >
                    <Option value="0-100000">₹0 - ₹1 Lakh</Option>
                    <Option value="100000-500000">₹1 Lakh - ₹5 Lakh</Option>
                    <Option value="500000-1000000">₹5 Lakh - ₹10 Lakh</Option>
                    <Option value="1000000+">₹10 Lakh+</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                  />
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default WorkExecutionUpdates;
